import { Component, EventEmitter, Input, OnInit, OnChanges, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { enums } from '../../../models/dashboard/job-offer/job-offer-enums';

// Available driver's license types
export const DRIVERS_LICENSE_TYPES = enums.driversLicenseTypes;
export type DriversLicenseType = (typeof DRIVERS_LICENSE_TYPES)[number];

@Component({
  selector: 'card-item-drivers-license',
  templateUrl: './card-item-drivers-license.component.html',
  styleUrls: ['./card-item-drivers-license.component.scss'],
  standalone: false,
})
export class CardItemDriversLicenseComponent implements OnInit, OnChanges {
  @Input() driversLicenses: any = null;
  @Input() candidateId: string;
  @Input() isAnonymized = false;
  @Output() save: EventEmitter<string[]> = new EventEmitter<string[]>();

  // All available license types
  licenseTypes = DRIVERS_LICENSE_TYPES;

  // Selected licenses
  selectedLicenses: Set<string> = new Set();

  // Edit mode state
  isEditing = false;

  // Temporary selection during edit
  tempSelectedLicenses: Set<string> = new Set();

  title = 'dashboard.candidateDetail.driversLicenses';

  constructor(private translate: TranslateService) {}

  ngOnInit(): void {
    this.parseExistingLicenses();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['driversLicenses']) {
      this.parseExistingLicenses();
    }
  }

  /**
   * Parse the existing drivers licenses from the input
   */
  private parseExistingLicenses(): void {
    this.selectedLicenses.clear();

    if (!this.driversLicenses) return;

    let licensesString = this.driversLicenses;
    if (typeof this.driversLicenses === 'object' && this.driversLicenses.driversLicenses) {
      licensesString = this.driversLicenses.driversLicenses;
    }

    if (!licensesString || licensesString === '[]') return;

    try {
      const licenses: string[] = typeof licensesString === 'string' ? JSON.parse(licensesString) : licensesString;
      licenses.forEach((license) => this.selectedLicenses.add(license));
    } catch {
      // If parsing fails, the string might already be a single license
      if (typeof licensesString === 'string') {
        this.selectedLicenses.add(licensesString);
      }
    }
  }

  /**
   * Get selected licenses as an array for display
   */
  getSelectedLicensesArray(): string[] {
    return Array.from(this.selectedLicenses).sort((a, b) => {
      return DRIVERS_LICENSE_TYPES.indexOf(a as DriversLicenseType) - DRIVERS_LICENSE_TYPES.indexOf(b as DriversLicenseType);
    });
  }

  /**
   * Check if a license is selected (during edit mode)
   */
  isLicenseSelected(license: string): boolean {
    return this.isEditing ? this.tempSelectedLicenses.has(license) : this.selectedLicenses.has(license);
  }

  /**
   * Toggle license selection (during edit mode)
   */
  toggleLicense(license: string): void {
    if (!this.isEditing) return;

    if (this.tempSelectedLicenses.has(license)) {
      this.tempSelectedLicenses.delete(license);
    } else {
      this.tempSelectedLicenses.add(license);
    }
  }

  /**
   * Enter edit mode
   */
  startEditing(): void {
    this.isEditing = true;
    this.tempSelectedLicenses = new Set(this.selectedLicenses);
  }

  /**
   * Cancel editing and revert changes
   */
  cancelEditing(): void {
    this.isEditing = false;
    this.tempSelectedLicenses.clear();
  }

  /**
   * Save the changes
   */
  saveChanges(): void {
    const licensesArray = Array.from(this.tempSelectedLicenses).sort((a, b) => {
      return DRIVERS_LICENSE_TYPES.indexOf(a as DriversLicenseType) - DRIVERS_LICENSE_TYPES.indexOf(b as DriversLicenseType);
    });

    this.save.emit(licensesArray);

    this.selectedLicenses = new Set(this.tempSelectedLicenses);
    this.isEditing = false;
    this.tempSelectedLicenses.clear();
  }

  /**
   * Check if there are any licenses to display
   */
  hasLicenses(): boolean {
    return this.selectedLicenses.size > 0;
  }
}
